import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Trash2, Plus, Search } from "lucide-react";

const CATEGORIES = ["all", "lead", "volunteer", "donor", "retreat_participant", "sponsor", "teacher", "partner", "investor", "media"];

export default function CRM() {
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/crm/contacts", { params: { category: cat, q: q || undefined } });
    setItems(data);
  }, [cat, q]);

  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!confirm("Delete this contact?")) return;
    await api.delete(`/crm/contacts/${id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline">CRM</div>
          <h1 className="font-serif text-4xl mt-1">Contacts</h1>
        </div>
        <NewContact onCreated={load} />
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" />
          <Input placeholder="Search by name or email" data-testid="crm-search" value={q} onChange={e=>setQ(e.target.value)} className="pl-9" />
        </div>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="w-52" data-testid="crm-category-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c.replace("_"," ")}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-admin-surface border border-line rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-bone/50 text-ink-soft">
            <tr>
              {["Name", "Email", "Category", "Tags", "Country", ""].map(h => <th key={h} className="text-left px-4 py-3 font-medium uppercase tracking-widest text-xs">{h}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {items.map(c => (
              <tr key={c.id} className="hover:bg-bone/40" data-testid={`crm-row-${c.id}`}>
                <td className="px-4 py-3 font-medium text-ink">{c.name}</td>
                <td className="px-4 py-3 text-ink-soft">{c.email}</td>
                <td className="px-4 py-3"><Badge variant="secondary" className="bg-forest/10 text-forest">{c.category}</Badge></td>
                <td className="px-4 py-3">{(c.tags || []).map(t => <Badge key={t} variant="outline" className="mr-1 border-line">{t}</Badge>)}</td>
                <td className="px-4 py-3 text-ink-soft">{c.country || "—"}</td>
                <td className="px-4 py-3 text-right"><button onClick={() => del(c.id)} data-testid={`crm-delete-${c.id}`} className="text-ink-soft hover:text-terracotta"><Trash2 className="h-4 w-4" /></button></td>
              </tr>
            ))}
            {!items.length && <tr><td colSpan="6" className="text-center text-ink-soft py-10">No contacts yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function NewContact({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", email: "", phone: "", category: "lead", country: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/crm/contacts", f); toast.success("Contact added"); setOpen(false); onCreated(); setF({ name: "", email: "", phone: "", category: "lead", country: "", notes: "" }); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button className="pill pill-primary text-sm" data-testid="crm-new-btn"><Plus className="h-4 w-4" /> New Contact</button></DialogTrigger>
      <DialogContent className="bg-bone">
        <DialogHeader><DialogTitle className="font-serif text-2xl">New Contact</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="overline">Name</Label><Input required data-testid="crm-form-name" value={f.name} onChange={e=>setF({...f, name: e.target.value})} /></div>
            <div><Label className="overline">Email</Label><Input type="email" required data-testid="crm-form-email" value={f.email} onChange={e=>setF({...f, email: e.target.value})} /></div>
            <div><Label className="overline">Phone</Label><Input data-testid="crm-form-phone" value={f.phone} onChange={e=>setF({...f, phone: e.target.value})} /></div>
            <div><Label className="overline">Country</Label><Input data-testid="crm-form-country" value={f.country} onChange={e=>setF({...f, country: e.target.value})} /></div>
          </div>
          <div>
            <Label className="overline">Category</Label>
            <Select value={f.category} onValueChange={(v)=>setF({...f, category: v})}>
              <SelectTrigger data-testid="crm-form-category"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.filter(c => c !== "all").map(c => <SelectItem key={c} value={c}>{c.replace("_"," ")}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="overline">Notes</Label><Textarea rows={3} data-testid="crm-form-notes" value={f.notes} onChange={e=>setF({...f, notes: e.target.value})} /></div>
          <button type="submit" disabled={busy} data-testid="crm-form-submit" className="pill pill-primary">{busy ? "Saving…" : "Save"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
